import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  fetchEmailQuotaStatus,
  type EmailQuotaStatus,
  type QuotaAlertLevel,
} from "../../api/emailQuotaApi";
import { cn } from "@/lib/utils";

const alertBadgeClass = (level: QuotaAlertLevel) => {
  switch (level) {
    case "CRITICAL":
      return "bg-red-100 text-red-800 border-red-200";
    case "WARN":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "INFO":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "OVERFLOW_ACTIVE":
      return "bg-purple-100 text-purple-800 border-purple-200";
    default:
      return "bg-green-100 text-green-800 border-green-200";
  }
};

const ProgressBar = ({ used, cap, tone }: { used: number; cap: number; tone?: "warn" | "critical" }) => {
  const pct = cap > 0 ? Math.min((used / cap) * 100, 100) : 0;
  return (
    <div className="h-2 w-full rounded bg-muted overflow-hidden">
      <div
        className={cn(
          "h-full rounded transition-all",
          tone === "critical" ? "bg-red-500" : tone === "warn" ? "bg-orange-500" : "bg-blue-500"
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const formatReset = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

type Props = {
  compact?: boolean;
  linkable?: boolean;
};

const EmailQuotaSummaryCard = ({ compact = false, linkable = true }: Props) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<EmailQuotaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchEmailQuotaStatus();
        if (!cancelled) {
          setStatus(data.status);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load quota");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const borderClass =
    status?.alertLevel === "CRITICAL"
      ? "border-red-400"
      : status?.alertLevel === "WARN"
        ? "border-orange-400"
        : "";

  return (
    <Card
      className={cn(linkable && "cursor-pointer transition-colors hover:border-primary", borderClass)}
      onClick={linkable ? () => navigate("/admin/email/quota") : undefined}
      role={linkable ? "button" : undefined}
      tabIndex={linkable ? 0 : undefined}
      onKeyDown={
        linkable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") navigate("/admin/email/quota");
            }
          : undefined
      }
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Resend e-mailquota
        </CardTitle>
        {status?.alertLevel && status.alertLevel !== "OK" ? (
          <Badge className={alertBadgeClass(status.alertLevel)}>{status.alertLevel}</Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : status ? (
          <>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Dag (UTC)</span>
                <span>
                  {status.day.used} / {status.day.hardCap} ({status.day.percent}%)
                </span>
              </div>
              <ProgressBar
                used={status.day.used}
                cap={status.day.hardCap}
                tone={status.day.percent >= 90 ? "critical" : status.day.percent >= 80 ? "warn" : undefined}
              />
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Maand (UTC)</span>
                <span>
                  {status.month.used} / {status.month.hardCap} ({status.month.percent}%)
                </span>
              </div>
              <ProgressBar
                used={status.month.used}
                cap={status.month.hardCap}
                tone={status.month.percent >= 90 ? "critical" : status.month.percent >= 80 ? "warn" : undefined}
              />
            </div>
            {!compact ? (
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>
                  Provider: <span className="font-medium text-foreground">{status.activeProvider || "resend"}</span>
                </div>
                <div>
                  Projectie: <span className="font-medium text-foreground">~{status.projectedMonth}</span>
                </div>
                <div>Dag reset: {formatReset(status.day.resetAt)}</div>
                <div>Maand reset: {formatReset(status.month.resetAt)}</div>
                {status.overflowActive ? (
                  <div className="col-span-2 flex items-center gap-1 text-purple-700">
                    <AlertTriangle className="h-3 w-3" />
                    Overflow actief ({status.overflowCountThisMonth} via Mailjet)
                  </div>
                ) : null}
              </div>
            ) : null}
            <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={(e) => { e.stopPropagation(); navigate("/admin/email"); }}>
              Email hub →
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default EmailQuotaSummaryCard;
